import { THREADS_GROUP } from "@kwp/firestore";
import { collectionGroup, getCountFromServer, limit, onSnapshot, orderBy, query, where, type QueryConstraint } from "firebase/firestore";
import { useEffect, useState } from "react";
import { useClients } from "../../lib/clients";
import { KIND_VIEWS } from "./kinds";

/**
 * The sidebar's unread counts on Mail (architecture §6.7, WP-064): per kind and for GitHub, how
 * many inbox conversations are unread in the scope. One `limit(1)` listener per kind on its newest
 * inbox thread (GitHub's is `source.app == github`); each time one fires, its unread count is read
 * again with a count aggregation, never the threads. A kind with no inbox thread counts 0 unread.
 */
export type CountKey = (typeof KIND_VIEWS)[number]["key"] | "github";
/** A kind's unread count; null while loading or when it could not be read. */
export type KindCounts = Record<CountKey, number | null>;

const KEYS: readonly CountKey[] = [...KIND_VIEWS.map((k) => k.key), "github"];

export const NO_COUNTS = (): KindCounts => ({ person: null, notification: null, newsletter: null, receipt: null, github: null });

/** All mail's count: the four kinds' sum (GitHub's are notifications), null until they are all read. */
export function allCount(counts: KindCounts): number | null {
  let sum = 0;
  for (const k of KIND_VIEWS) {
    const n = counts[k.key];
    if (n === null) return null;
    sum += n;
  }
  return sum;
}

/** `accountIds` null: every account; an empty list has nothing to count; undefined: the scope is still loading. */
export function useKindCounts(uid: string, accountIds: readonly string[] | null | undefined, enabled: boolean): KindCounts {
  const { firebase } = useClients();
  const key = accountIds === undefined ? undefined : JSON.stringify(accountIds);
  const [counts, setCounts] = useState<KindCounts>(NO_COUNTS);

  useEffect(() => {
    setCounts(NO_COUNTS());
    if (!enabled || key === undefined) return undefined;
    const ids = JSON.parse(key) as string[] | null;
    if (ids !== null && ids.length === 0) {
      setCounts({ person: 0, notification: 0, newsletter: 0, receipt: 0, github: 0 });
      return undefined;
    }
    const scope: QueryConstraint[] = [where("ownerUid", "==", uid)];
    if (ids !== null) scope.push(ids.length === 1 ? where("accountId", "==", ids[0]) : where("accountId", "in", ids));
    let live = true;
    // Only the newest fire's count lands: an older count that answers late is dropped.
    const fired: Partial<Record<CountKey, number>> = {};
    const set = (k: CountKey, n: number | null, fire: number | undefined) => {
      if (live && fired[k] === fire) setCounts((c) => ({ ...c, [k]: n }));
    };
    const unsubscribes = KEYS.map((k) => {
      const base = [...scope, where("location", "==", "inbox"), k === "github" ? where("source.app", "==", "github") : where("kind", "==", k)];
      return onSnapshot(
        query(collectionGroup(firebase.db, THREADS_GROUP), ...base, orderBy("lastMessageAt", "desc"), limit(1)),
        (snap) => {
          const fire = (fired[k] ?? 0) + 1;
          fired[k] = fire;
          if (snap.size === 0) {
            set(k, 0, fire);
            return;
          }
          void getCountFromServer(query(collectionGroup(firebase.db, THREADS_GROUP), ...base, where("unread", "==", true))).then(
            (c) => set(k, c.data().count, fire),
            () => set(k, null, fire),
          );
        },
        () => set(k, null, fired[k]),
      );
    });
    return () => {
      live = false;
      unsubscribes.forEach((u) => u());
    };
  }, [firebase.db, uid, key, enabled]);

  return counts;
}
